import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import { tabsClasses } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import SideMenuMobile from '../components/SideMenuMobile';
import MenuButton from '../components/MenuButton';
import ColorModeIconDropdown from '../theme/ColorModeIconDropdown';
import OptionsMenu from '../components/OptionsMenu';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';

const Toolbar = styled(MuiToolbar)({
    width: '100%',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'center',
    gap: '12px',
    flexShrink: 0,
    [`& ${tabsClasses.flexContainer}`]: {
        gap: '8px',
        p: '8px',
        pb: 0,
    },
});

export default function AppNavbar() {
    const location = useLocation();
    const { _getUser } = useAuth();
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState(_getUser() || null);
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    // Check if token is in URL and fetch user data if needed
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tokenFromUrl = urlParams.get('token');
        
        const fetchUserFromToken = async () => {
            if (tokenFromUrl) {
                try {
                    // Fetch user data using the token from URL
                    const response = await api.get('user/me');
                    const userData = response.data.data;
                    // Store in component state only (not localStorage to avoid affecting super admin tab)
                    setUser(userData);
                } catch (error) {
                    console.error('Failed to fetch user from token:', error);
                    // Fallback to localStorage user if fetch fails
                    const storedUser = _getUser();
                    if (storedUser) {
                        setUser(storedUser);
                    }
                }
            } else {
                // Use user from localStorage if no token in URL
                const storedUser = _getUser();
                if (storedUser) {
                    setUser(storedUser);
                }
            }
        };

        fetchUserFromToken();
    }, [location.search, _getUser]);

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };

    const userName = user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';

    const handleAvatarClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                display: { xs: 'auto', md: 'none' },
                boxShadow: 0,
                bgcolor: 'background.paper',
                backgroundImage: 'none',
                borderBottom: '1px solid',
                borderColor: 'divider',
                top: 'var(--template-frame-height, 0px)',
            }}
        >
            <Toolbar variant="regular">
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        flexGrow: 1,
                        width: '100%',
                        gap: 1,
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ justifyContent: 'center', mr: 'auto' }}
                    >
                        <CustomIcon />
                        <Typography variant="h4" component="h1" sx={{ color: 'text.primary' }}>
                            Dashboard
                        </Typography>
                    </Stack>
                    <ColorModeIconDropdown />
                    {user && (
                        <>
                            <Avatar
                                sizes="small"
                                alt={userName}
                                src="/static/images/avatar/7.jpg"
                                sx={{ 
                                    width: 32, 
                                    height: 32,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        opacity: 0.8
                                    }
                                }}
                                onClick={handleAvatarClick}
                            />
                            <OptionsMenu 
                                user={user}
                                anchorEl={anchorEl}
                                open={menuOpen}
                                onClose={handleMenuClose}
                            />
                        </>
                    )}
                    <MenuButton aria-label="menu" onClick={toggleDrawer(true)}>
                        <MenuRoundedIcon />
                    </MenuButton>
                    <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
                </Stack>
            </Toolbar>
        </AppBar>
    );
}

export function CustomIcon() {
    return (
        <Box
            sx={{
                width: '1.5rem',
                height: '1.5rem',
                bgcolor: 'black',
                borderRadius: '999px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'center',
                backgroundImage:
                    'linear-gradient(135deg, hsl(210, 98%, 60%) 0%, hsl(210, 100%, 35%) 100%)',
                color: 'hsla(210, 100%, 95%, 0.9)',
                border: '1px solid',
                borderColor: 'hsl(210, 100%, 55%)',
                boxShadow: 'inset 0 2px 5px rgba(255, 255, 255, 0.3)',
            }}
        >
            <DashboardRoundedIcon color="inherit" sx={{ fontSize: '1rem' }} />
        </Box>
    );
}
